/* ==========================================================================
   Judge.me → Custom Premium Slider
   --------------------------------------------------------------------------
   Reads review data live from the Judge.me widget DOM and renders it into
   a touch/drag-enabled infinite slider. Nothing here is hardcoded —
   if Judge.me re-renders (new review approved, variant change, pagination),
   the slider rebuilds itself from whatever is in the DOM at that moment.
   ========================================================================== */

(function () {
  "use strict";

  /* ------------------------------------------------------------------------
     CONFIG — adjust selectors here if your Judge.me widget template uses
     different class names. Each entry tries several common possibilities
     in order, so you usually won't need to touch this.
     ------------------------------------------------------------------------ */
  var CONFIG = {
    // the element that wraps every review item
    listSelector: ".jdgm-review-list",
    // a single review item inside that list
    itemSelector: ".jm-review-item, .jdgm-rev",
    // inside one item:
    starsSelector: '[class*="star-rating"]',   // has data-score attribute

    // NAME: tried in priority order, most specific Judge.me class first.
    // The generic '[class*="author"]' / '[class*="reviewer"]' entries are
    // kept as a last resort because on some Judge.me templates they match
    // a WRAPPER element that also contains the icon-initial, the verified
    // badge and the date as children — that wrapper's text gets isolated
    // and cleaned up in extractNameAndDate() below, not read raw.
    nameSelectorCandidates: [
      ".jdgm-rev__author",
      ".jm-review-item__author",
      ".jm-review-item__name",
      '[class*="author"]',
      '[class*="reviewer-name"]',
      '[class*="reviewer"]'
    ],

    // DATE: same idea as name. On some templates there is no standalone
    // date node — the date text lives inside the same wrapper matched for
    // the name — so the date is *also* pulled out of that wrapper text as
    // a fallback (see extractNameAndDate()).
    dateSelectorCandidates: [
      ".jdgm-rev__date",
      ".jdgm-rev__timestamp",
      ".jm-review-item__date",
      '[class*="date"]',
      '[class*="timestamp"]'
    ],

    // small icon / initial-letter avatar element Judge.me sometimes places
    // directly inside the author wrapper (not an <img>) — needs to be
    // stripped out before reading the wrapper's "name" text
    iconSelector: '[class*="icon"], [class*="initial"]',

    // tried in order, most specific first — generic [class*="body"] /
    // [class*="content"] is a LAST resort since on some Judge.me templates
    // that generic class matches a wrapper containing name+date+body together
    bodySelectorCandidates: [
      ".jdgm-rev__body",
      ".jdgm-rev__text",
      ".jm-review-item__body",
      ".jm-review-item__text",
      ".jm-review-item__review-text",
      '[class*="review-body"]',
      '[class*="rev-body"]',
      '[class*="review-text"]',
      '[class*="rev-text"]',
      '[class*="body"]',
      '[class*="content"]'
    ],
    verifiedSelector: '[class*="verified"], [class*="badge"]',
    // AVATAR: scoped candidates tried in priority order, so we don't
    // accidentally grab an unrelated <img> (e.g. a badge icon) elsewhere
    // in the item. Plain "img" stays as the last-resort fallback.
    avatarSelectorCandidates: [
      ".jdgm-rev__icon img",
      ".jdgm-rev__avatar img",
      ".jm-review-item__avatar img",
      '[class*="avatar"] img',
      '[class*="icon"] img',
      "img"
    ],
    // attributes tried in order to find the real image URL — Judge.me
    // (and many themes) lazy-load avatars, so the real URL often sits in
    // data-src / data-original until the image scrolls into view, and
    // "src" alone can be empty or a tiny placeholder at build time.
    avatarSrcAttrCandidates: ["src", "data-src", "data-original", "data-lazy-src"],
    // breakpoints (px) -> cards visible
    breakpoints: { desktop: 1200, tablet: 768 },
    perView: { desktop: 4, tablet: 3, mobile: 2 },
    gap: { desktop: 24, tablet: 24, mobile: 16 },
    dragThreshold: 40 // px before a drag counts as a slide change
  };

  var SVG_CHECK =
    '<svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.7 8.6 2.1 6l-1 1 3.6 3.6L11 4.3l-1-1z"/></svg>';

  // Matches common Judge.me date formats: 08/18/2026, 2026-08-18,
  // August 18, 2026 / Aug 18 2026, etc.
  var DATE_PATTERN =
    /\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{2}-\d{2}|[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{4}/;

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  // Tries each selector in a candidate list, in priority order, and
  // returns the FIRST one that matches — unlike a comma-joined CSS
  // selector (which returns whichever matches first in document order,
  // ignoring priority).
  function qsFirst(root, candidates) {
    for (var i = 0; i < candidates.length; i++) {
      var el = qs(root, candidates[i]);
      if (el) return el;
    }
    return null;
  }

  function getScore(el) {
    if (!el) return 0;
    var score =
      el.getAttribute("data-score") ||
      el.getAttribute("data-rating") ||
      el.getAttribute("aria-label");
    if (!score) return 0;
    var n = parseFloat(score);
    return isNaN(n) ? 0 : Math.round(n);
  }

  function starString(score) {
    score = Math.max(0, Math.min(5, score));
    return "★★★★★☆☆☆☆☆".slice(5 - score, 10 - score);
  }

  function textOf(el) {
    return el ? el.textContent.replace(/\s+/g, " ").trim() : "";
  }

  function isVerified(item) {
    var el = qs(item, CONFIG.verifiedSelector);
    if (!el) return false;
    var t = textOf(el).toLowerCase();
    // a generic "badge" class might not mean "verified" — only count it if
    // it actually says so, or if the class name itself mentions "verified"
    return t.indexOf("verifi") !== -1 || el.className.toLowerCase().indexOf("verifi") !== -1;
  }

  function escapeHtml(str) {
    var d = document.createElement("div");
    d.textContent = str;
    return d.innerHTML;
  }

  function matchesAny(el, selector) {
    if (!el || !selector) return false;
    if (typeof el.matches === "function") return el.matches(selector);
    return false;
  }

  // Returns a CLONE of el with every node matching any selector in
  // `selectors` removed. Never touches the live DOM. Used to isolate the
  // "own" text of a wrapper element (e.g. an author node that also
  // contains an icon, a verified badge, or a date as children).
  function cloneWithoutSelectors(el, selectors) {
    var clone = el.cloneNode(true);
    selectors.forEach(function (sel) {
      if (!sel) return;
      qsa(clone, sel).forEach(function (node) {
        node.parentNode && node.parentNode.removeChild(node);
      });
    });
    return clone;
  }

  // Strips author/date/badge/avatar/star sub-nodes out of a CLONE of the
  // given element (never touches the live DOM), then returns the leftover
  // text — i.e. the actual review body, nothing else.
  function stripToBodyText(el) {
    if (!el) return "";
    var stripSelectors = [
      CONFIG.nameSelectorCandidates.join(","),
      CONFIG.dateSelectorCandidates.join(","),
      CONFIG.verifiedSelector,
      CONFIG.starsSelector,
      CONFIG.iconSelector,
      "img"
    ];
    var clone = cloneWithoutSelectors(el, stripSelectors);
    return textOf(clone);
  }

  // Tries each body candidate selector in priority order. A candidate is
  // rejected outright if the matched element IS itself the name/date/stars
  // element (a sign the generic selector grabbed the wrong node). The
  // surviving candidate is then run through stripToBodyText() as a safety
  // net in case it's a wrapper that still contains reviewer info inside it.
  function findBodyText(item, nameEl, dateEl, starsEl) {
    for (var i = 0; i < CONFIG.bodySelectorCandidates.length; i++) {
      var el = qs(item, CONFIG.bodySelectorCandidates[i]);
      if (!el) continue;
      if (el === nameEl || el === dateEl || el === starsEl) continue;
      if (
        matchesAny(el, CONFIG.nameSelectorCandidates.join(",")) ||
        matchesAny(el, CONFIG.dateSelectorCandidates.join(","))
      )
        continue;

      var text = stripToBodyText(el);
      if (text) return text;
    }
    // last-resort fallback: strip the whole item and use what's left
    return stripToBodyText(item);
  }

  // Extracts a clean reviewer name and a clean date, even when Judge.me's
  // template only exposes ONE wrapper element that contains the icon
  // initial, the author name, the verified badge AND the date all mashed
  // together as sibling/child text nodes (this is what was producing
  // "RRosy08/18/2026" and "AAnonymousVerified05/22/2026").
  function extractNameAndDate(item, starsEl) {
    var nameEl = qsFirst(item, CONFIG.nameSelectorCandidates);
    var dateEl = qsFirst(item, CONFIG.dateSelectorCandidates);
    var verifiedEl = qs(item, CONFIG.verifiedSelector);

    var commonStrip = [CONFIG.iconSelector, CONFIG.verifiedSelector, CONFIG.starsSelector, "img"];

    // --- name ---
    var name = "";
    if (nameEl) {
      var stripFromName = commonStrip.slice();
      if (dateEl && dateEl !== nameEl) {
        stripFromName.push(CONFIG.dateSelectorCandidates.join(","));
      }
      name = textOf(cloneWithoutSelectors(nameEl, stripFromName));
    }

    // --- date ---
    var date = "";
    if (dateEl) {
      var stripFromDate = commonStrip.slice();
      if (nameEl && nameEl !== dateEl) {
        stripFromDate.push(CONFIG.nameSelectorCandidates.join(","));
      }
      date = textOf(cloneWithoutSelectors(dateEl, stripFromDate));
    }

    // Safety net: if name/date came from the SAME wrapper (or the date
    // node wasn't found separately at all), pull the date out of the raw
    // name text with a regex and strip it back out of the name.
    var dateMatch = name.match(DATE_PATTERN);
    if (dateMatch) {
      if (!date) date = dateMatch[0];
      name = (name.slice(0, dateMatch.index) + name.slice(dateMatch.index + dateMatch[0].length)).trim();
    }

    // Safety net: strip a leading duplicated initial letter left over from
    // an icon/avatar element that wasn't caught by iconSelector
    // (e.g. "RRosy" -> "Rosy", "AAnonymous" -> "Anonymous").
    if (name.length > 2 && name.charAt(0) === name.charAt(1) && /[A-Za-z]/.test(name.charAt(0))) {
      name = name.slice(1);
    }

    // Safety net: strip a trailing "Verified" / "Verified Buyer" badge
    // word glued directly onto the name.
    name = name.replace(/\s*Verified(\s*Buyer)?\s*$/i, "").trim();

    name = name.trim() || "Customer";
    date = date.trim();

    return { name: name, date: date, nameEl: nameEl, dateEl: dateEl, verifiedEl: verifiedEl };
  }

  // Finds the review's avatar <img> (scoped to avoid grabbing an unrelated
  // image elsewhere in the item) and reads its real URL, trying lazy-load
  // attributes in order. Skips empty values and inline base64 placeholder
  // images (a common lazy-load "blank" stand-in), which is what a plain
  // `img.getAttribute("src")` read was silently returning before.
  function getAvatarSrc(item) {
    var imgEl = qsFirst(item, CONFIG.avatarSelectorCandidates);
    if (!imgEl) return null;

    for (var i = 0; i < CONFIG.avatarSrcAttrCandidates.length; i++) {
      var attr = CONFIG.avatarSrcAttrCandidates[i];
      var val = imgEl.getAttribute(attr);
      if (val) {
        val = val.trim();
        if (val && val.indexOf("data:image") !== 0) return val;
      }
    }

    // last resort: a srcset attribute, take the first URL in the list
    var srcset = imgEl.getAttribute("srcset") || imgEl.getAttribute("data-srcset");
    if (srcset) {
      var first = srcset.split(",")[0].trim().split(" ")[0];
      if (first && first.indexOf("data:image") !== 0) return first;
    }

    return null;
  }

  /* ------------------------------------------------------------------------
     Parse one Judge.me review item into a plain data object
     ------------------------------------------------------------------------ */

  function parseReview(item) {
    var starsEl = qs(item, CONFIG.starsSelector);
    var extracted = extractNameAndDate(item, starsEl);

    return {
      score: getScore(starsEl),
      name: extracted.name,
      initial: extracted.name.charAt(0),
      date: extracted.date,
      body: findBodyText(item, extracted.nameEl, extracted.dateEl, starsEl),
      verified: isVerified(item),
      avatarSrc: getAvatarSrc(item)
    };
  }

  /* ------------------------------------------------------------------------
     Build one card's markup from a parsed review
     ------------------------------------------------------------------------ */

  function buildCard(review) {
    var card = document.createElement("div");
    card.className = "jmcs-card";

    var avatarInner = review.avatarSrc
      ? '<img src="' + escapeHtml(review.avatarSrc) + '" alt="">'
      : escapeHtml(review.initial);

    card.innerHTML =
      '<div class="jmcs-card__stars" aria-label="' + review.score + ' out of 5 stars">' +
        starString(review.score) +
      "</div>" +
      '<p class="jmcs-card__text">' + escapeHtml(review.body) + "</p>" +
      '<div class="jmcs-card__footer">' +
        '<div class="jmcs-card__avatar">' + avatarInner + "</div>" +
        '<div class="jmcs-card__meta">' +
          '<div class="jmcs-card__name-row">' +
            '<span class="jmcs-card__name">' + escapeHtml(review.name) + "</span>" +
            (review.verified
              ? '<span class="jmcs-card__verified" title="Verified buyer">' + SVG_CHECK + "</span>"
              : "") +
          "</div>" +
          '<div class="jmcs-card__date">' + escapeHtml(review.date) + "</div>" +
        "</div>" +
      "</div>";

    return card;
  }

  /* ------------------------------------------------------------------------
     Slider class — handles responsive perView, infinite loop via clones,
     drag/swipe, and dot pagination.
     ------------------------------------------------------------------------ */

  function Slider(container, reviews) {
    this.container = container;
    this.reviews = reviews;
    this.count = reviews.length;
    this.index = 0;
    this.perView = this.getPerView();
    this.gap = this.getGap();
    this.dragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.baseTranslate = 0;

    this.build();
    this.bindEvents();
  }

  Slider.prototype.getPerView = function () {
    var w = window.innerWidth;
    if (w >= CONFIG.breakpoints.desktop) return CONFIG.perView.desktop;
    if (w >= CONFIG.breakpoints.tablet) return CONFIG.perView.tablet;
    return CONFIG.perView.mobile;
  };

  Slider.prototype.getGap = function () {
    var w = window.innerWidth;
    if (w >= CONFIG.breakpoints.desktop) return CONFIG.gap.desktop;
    if (w >= CONFIG.breakpoints.tablet) return CONFIG.gap.tablet;
    return CONFIG.gap.mobile;
  };

  Slider.prototype.build = function () {
    this.container.innerHTML = "";

    var wrap = document.createElement("div");
    wrap.className = "jmcs-slider";

    var viewport = document.createElement("div");
    viewport.className = "jmcs-viewport";

    var track = document.createElement("div");
    track.className = "jmcs-track";

    this.canLoop = this.count > this.perView;

    // build slide list: clone tail+head when looping is possible
    var slides = this.reviews.slice();
    if (this.canLoop) {
      var head = this.reviews.slice(0, this.perView);
      var tail = this.reviews.slice(-this.perView);
      slides = tail.concat(this.reviews, head);
      this.index = this.perView; // start on the first "real" slide
    } else {
      this.index = 0;
    }

    this.slideEls = slides.map(function (r) {
      return buildCard(r);
    });
    this.slideEls.forEach(function (el) {
      track.appendChild(el);
    });

    viewport.appendChild(track);
    wrap.appendChild(viewport);

    if (this.count > 1) {
      var dots = document.createElement("div");
      dots.className = "jmcs-dots";
      var self = this;
      this.dotEls = this.reviews.map(function (_, i) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "jmcs-dot";
        dot.setAttribute("aria-label", "Go to review " + (i + 1));
        dot.addEventListener("click", function () {
          self.goToReal(i);
        });
        dots.appendChild(dot);
        return dot;
      });
      wrap.appendChild(dots);
    }

    this.container.appendChild(wrap);

    this.viewport = viewport;
    this.track = track;
    this.setCardWidth();
    this.setTranslate(false);
    this.updateDots();
  };

  Slider.prototype.setCardWidth = function () {
    var vpWidth = this.viewport.clientWidth;
    var gap = this.gap;
    this.track.style.gap = gap + "px";
    this.cardWidth = (vpWidth - gap * (this.perView - 1)) / this.perView;
    this.slideEls.forEach(
      function (el) {
        el.style.width = this.cardWidth + "px";
      }.bind(this)
    );
  };

  Slider.prototype.stepSize = function () {
    return this.cardWidth + this.gap;
  };

  Slider.prototype.setTranslate = function (animate) {
    this.track.classList.toggle("jmcs-no-transition", !animate);
    var x = -this.index * this.stepSize();
    this.track.style.transform = "translateX(" + x + "px)";
  };

  Slider.prototype.realIndex = function () {
    if (!this.canLoop) return this.index;
    var real = (this.index - this.perView) % this.count;
    if (real < 0) real += this.count;
    return real;
  };

  Slider.prototype.updateDots = function () {
    if (!this.dotEls) return;
    var real = this.realIndex();
    this.dotEls.forEach(function (dot, i) {
      dot.classList.toggle("jmcs-dot--active", i === real);
    });
  };

  Slider.prototype.goToReal = function (i) {
    this.index = this.canLoop ? i + this.perView : i;
    this.setTranslate(true);
    this.updateDots();
  };

  Slider.prototype.next = function () {
    this.move(1);
  };
  Slider.prototype.prev = function () {
    this.move(-1);
  };

  Slider.prototype.move = function (dir) {
    if (!this.canLoop && (
      (dir > 0 && this.index >= this.count - this.perView) ||
      (dir < 0 && this.index <= 0)
    )) {
      // no loop, and we're at an edge — just snap back
      this.setTranslate(true);
      return;
    }
    this.index += dir;
    this.setTranslate(true);
    this.updateDots();

    if (this.canLoop) {
      var self = this;
      var maxIndex = this.perView + this.count - 1;
      var minIndex = this.perView;
      if (this.index > maxIndex || this.index < minIndex - this.perView) {
        // handled by the transitionend snap below
      }
      this.handleLoopBoundary();
    }
  };

  Slider.prototype.handleLoopBoundary = function () {
    var self = this;
    var onEnd = function () {
      self.track.removeEventListener("transitionend", onEnd);
      var total = self.slideEls.length;
      if (self.index >= total - self.perView) {
        self.index = self.perView + ((self.index - self.perView) % self.count);
        self.setTranslate(false);
      } else if (self.index < self.perView - self.perView) {
        self.index = self.perView + self.count - ((self.perView - self.index) % self.count);
        self.setTranslate(false);
      }
    };
    this.track.addEventListener("transitionend", onEnd);
  };

  Slider.prototype.bindEvents = function () {
    var self = this;

    // resize: recompute perView + card widths, rebuild if perView changed
    window.addEventListener(
      "resize",
      debounce(function () {
        var newPerView = self.getPerView();
        self.gap = self.getGap();
        if (newPerView !== self.perView) {
          self.perView = newPerView;
          self.build();
        } else {
          self.setCardWidth();
          self.setTranslate(false);
        }
      }, 150)
    );

    // pointer-based drag (covers mouse + touch + pen)
    var dragStartX = 0;
    var dragDelta = 0;

    function onPointerDown(e) {
      self.dragging = true;
      self.viewport.classList.add("jmcs-dragging");
      dragStartX = (e.touches ? e.touches[0].clientX : e.clientX);
      dragDelta = 0;
      self.track.classList.add("jmcs-no-transition");
    }

    function onPointerMove(e) {
      if (!self.dragging) return;
      var x = (e.touches ? e.touches[0].clientX : e.clientX);
      dragDelta = x - dragStartX;
      var base = -self.index * self.stepSize();
      self.track.style.transform = "translateX(" + (base + dragDelta) + "px)";
    }

    function onPointerUp() {
      if (!self.dragging) return;
      self.dragging = false;
      self.viewport.classList.remove("jmcs-dragging");
      self.track.classList.remove("jmcs-no-transition");

      if (Math.abs(dragDelta) > CONFIG.dragThreshold) {
        self.move(dragDelta < 0 ? 1 : -1);
      } else {
        self.setTranslate(true);
      }
      dragDelta = 0;
    }

    this.viewport.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);

    this.viewport.addEventListener("touchstart", onPointerDown, { passive: true });
    this.viewport.addEventListener("touchmove", onPointerMove, { passive: true });
    this.viewport.addEventListener("touchend", onPointerUp);
  };

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  /* ------------------------------------------------------------------------
     Bootstrap: find the Judge.me widget, build/rebuild the slider, and
     keep watching the DOM in case Judge.me re-renders it later.
     ------------------------------------------------------------------------ */

  var sliderContainer = null;
  var currentList = null;

  function init() {
    var list = document.querySelector(CONFIG.listSelector);
    if (!list) return false;

    // avoid re-processing the same already-converted list
    if (list === currentList && list.classList.contains("jmcs-source")) {
      return true;
    }

    var items = qsa(list, CONFIG.itemSelector);
    if (!items.length) return false;

    var reviews = items.map(parseReview);

    // mark + visually hide the original (kept in DOM for SEO/schema)
    list.classList.add("jmcs-source");
    currentList = list;

    // create (or reuse) the slider mount point right after the original list
    if (!sliderContainer || !sliderContainer.isConnected) {
      sliderContainer = document.createElement("div");
      sliderContainer.className = "jmcs-mount";
      list.parentNode.insertBefore(sliderContainer, list.nextSibling);
    }

    if (!reviews.length) {
      sliderContainer.innerHTML = '<p class="jmcs-empty">No reviews yet.</p>';
      return true;
    }

    new Slider(sliderContainer, reviews);
    return true;
  }

  function bootWithRetry() {
    if (init()) return;
    // Judge.me loads its widget asynchronously — poll briefly until it appears
    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      if (init() || attempts > 40) clearInterval(poll); // ~10s max
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootWithRetry);
  } else {
    bootWithRetry();
  }

  // Watch for Judge.me re-rendering the widget (new review approved,
  // variant switch, "load more", etc.) and rebuild the slider to match.
  var observerTarget = document.body;
  var mo = new MutationObserver(
    debounce(function () {
      init();
    }, 300)
  );
  mo.observe(observerTarget, { childList: true, subtree: true });
})();